import { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, brl, fmtDate } from "./api";

const inputCls = "w-full bg-white border border-military/20 rounded-lg px-3 py-2.5 text-sm text-military-dark focus:outline-none focus:border-military";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] font-semibold text-military-dark/50 mb-1.5";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [sale, setSale] = useState({ client: "", slug: "", variant: "", qty: 1, value: "", payment: "Pix", note: "", date: "" });

  const load = () => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const pick = (slug) => {
    const p = products.find((x) => x.slug === slug);
    const v = p?.variants?.[0] || null;
    const unit = v ? (v.sale_price ?? v.price ?? p.price) : (p ? (p.sale_price ?? p.price) : 0);
    setSale((s) => ({ ...s, slug, variant: v?.name || "", value: p ? String(unit * s.qty) : s.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/sales", {
        client: sale.client, slug: sale.slug, variant: sale.variant || null, qty: Number(sale.qty), value: Number(sale.value),
        payment: sale.payment, note: sale.note, date: sale.date ? new Date(sale.date).toISOString() : null,
      });
      toast.success("Venda registrada e estoque atualizado");
      setOpen(false);
      setSale({ client: "", slug: "", qty: 1, value: "", payment: "Pix", note: "", date: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h1 className="font-serif text-3xl md:text-4xl text-military-dark">Pedidos & Vendas</h1>
        <button data-testid="register-whatsapp-sale-button" onClick={() => setOpen(true)} className="flex items-center gap-2 rounded-full bg-military text-offwhite px-5 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors">
          <MessageCircle size={14} /> Registrar venda pelo WhatsApp
        </button>
      </div>

      <div className="space-y-3">
        {orders.map((o) => (
          <div key={o.id} data-testid={`order-${o.code}`} className="bg-white border border-military/10 rounded-lg p-5">
            <div className="flex flex-wrap justify-between gap-3 mb-2">
              <div>
                <p className="font-semibold text-military-dark text-sm">
                  {o.code}
                  <span className={`ml-2 text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${o.origin === "whatsapp" || o.origin === "whatsapp_auto" ? "bg-military/10 text-military" : "bg-baby text-military-dark"}`}>{o.origin === "whatsapp" ? "WhatsApp" : o.origin === "whatsapp_auto" ? "WhatsApp auto" : "Site"}</span>
                  {o.origin === "whatsapp_auto" && !o.stock_deducted && o.status !== "cancelado" && <span className="ml-2 text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">estoque não baixado</span>}
                </p>
                <p className="text-xs text-military-dark/50 mt-0.5">{o.customer?.name || "—"} · {fmtDate(o.created_at)}{o.payment ? ` · ${o.payment}` : ""}</p>
              </div>
              <div className="flex items-center gap-3">
                <p className="font-serif text-xl text-military-dark">{brl(o.total)}</p>
                <select
                  data-testid={`status-${o.code}`}
                  value={o.status || "pago"}
                  onChange={(e) => api.put(`/admin/orders/${o.id}/status`, { status: e.target.value }).then(load).catch((err) => toast.error(apiError(err)))}
                  className={`text-[10px] uppercase tracking-wider font-semibold rounded-full px-3 py-1.5 border-0 cursor-pointer ${
                    (o.status || "pago") === "pago" ? "bg-military/10 text-military" : o.status === "cancelado" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                  }`}
                >
                  <option value="pago">Pago</option>
                  <option value="pendente">Pendente</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </div>
            <ul className="text-xs text-military-dark/60 space-y-0.5">
              {(o.items || []).map((i, idx) => <li key={idx}>{i.qty}x {i.name} — {brl(i.price * i.qty)}</li>)}
            </ul>
            {o.note && <p className="text-xs text-military-dark/50 mt-2 italic">{o.note}</p>}
          </div>
        ))}
        {orders.length === 0 && <p className="text-sm text-military-dark/40">Nenhum pedido registrado ainda.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-military-dark/40 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={() => setOpen(false)}>
          <form data-testid="whatsapp-sale-form" onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-offwhite w-full md:max-w-md max-h-[90vh] overflow-y-auto rounded-t-lg md:rounded-lg p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-military-dark">Venda pelo WhatsApp</h2>
              <button type="button" onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-baby"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div><label className={labelCls}>Cliente</label><input data-testid="sale-client" required value={sale.client} onChange={(e) => setSale({ ...sale, client: e.target.value })} className={inputCls} /></div>
              <div>
                <label className={labelCls}>Produto</label>
                <select data-testid="sale-product" required value={sale.slug} onChange={(e) => pick(e.target.value)} className={inputCls}>
                  <option value="">Selecione...</option>
                  {products.map((p) => <option key={p.slug} value={p.slug}>{p.name} (estoque: {p.stock})</option>)}
                </select>
              </div>
              {(() => {
                const sel = products.find((x) => x.slug === sale.slug);
                return sel?.variants?.length ? (
                  <div>
                    <label className={labelCls}>Sabor</label>
                    <select data-testid="sale-variant" required value={sale.variant} onChange={(e) => {
                      const v = sel.variants.find((x) => x.name === e.target.value);
                      const unit = v ? (v.sale_price ?? v.price ?? sel.price) : sel.price;
                      setSale({ ...sale, variant: e.target.value, value: String(unit * sale.qty) });
                    }} className={inputCls}>
                      {sel.variants.map((v) => <option key={v.name} value={v.name}>{v.name} (estoque: {v.stock})</option>)}
                    </select>
                  </div>
                ) : null;
              })()}
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>Quantidade</label><input data-testid="sale-qty" required type="number" min="1" value={sale.qty} onChange={(e) => setSale({ ...sale, qty: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>Valor total (R$)</label><input data-testid="sale-value" required type="number" step="0.01" min="0" value={sale.value} onChange={(e) => setSale({ ...sale, value: e.target.value })} className={inputCls} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Pagamento</label>
                  <select data-testid="sale-payment" value={sale.payment} onChange={(e) => setSale({ ...sale, payment: e.target.value })} className={inputCls}>
                    {["Pix", "Cartão", "Dinheiro", "Link de pagamento"].map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Data</label><input data-testid="sale-date" type="datetime-local" value={sale.date} onChange={(e) => setSale({ ...sale, date: e.target.value })} className={inputCls} /></div>
              </div>
              <div><label className={labelCls}>Observação</label><input data-testid="sale-note" value={sale.note} onChange={(e) => setSale({ ...sale, note: e.target.value })} className={inputCls} /></div>
            </div>
            <button data-testid="sale-save" type="submit" className="mt-6 w-full rounded-full bg-military-dark text-offwhite py-3.5 text-sm uppercase tracking-widest font-semibold hover:bg-military transition-colors">
              Confirmar venda e dar baixa no estoque
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
